<template>
  <div class="concepts-section mb-4">
    <div class="section-title-container d-flex align-items-center justify-content-between mb-3">
      <div class="d-flex align-items-center">
        <i class="fas fa-shopping-basket me-2 section-icon"></i>
        <h5 class="section-title m-0 fw-bold">CONCEPTOS A PAGAR</h5>
      </div>
      <span class="text-muted-mini">Seleccione en orden secuencial</span>
    </div>
    
    <div class="row g-3">
      <!-- Loading skeleton -->
      <template v-if="loading">
        <div class="col-6" v-for="i in 2" :key="'skeleton-' + i">
          <div class="concept-card skeleton-card p-3">
            <div class="skeleton-line mb-2" style="width: 50%; height: 16px;"></div>
            <div class="skeleton-line" style="width: 30%; height: 20px;"></div>
          </div>
        </div>
      </template>
      
      <!-- Actual Installments/Concepts -->
      <template v-else-if="concepts.length > 0">
        <div class="col-6 col-md-6" v-for="(item, index) in concepts" :key="item.id">
          <div 
            class="concept-card p-3 d-flex flex-column justify-content-between h-100 position-relative"
            :class="{ 
              'selected': isSelected(item.id),
              'disabled': !isItemAvailable(item, index) || item.paidStatus === 'paid',
              'paid': item.paidStatus === 'paid'
            }"
            @click="toggleSelection(item)"
          >
            <div class="d-flex align-items-start justify-content-between">
              <div>
                <span class="concept-name fw-bold">{{ item.name }}</span>
                <span v-if="item.dueDate" class="concept-due-date text-muted d-block mt-1">
                  Vence: {{ formatDueDate(item.dueDate) }}
                </span>
              </div>
              <div class="check-indicator d-flex align-items-center justify-content-center" v-if="item.paidStatus !== 'paid'">
                <i class="fas fa-check check-icon" v-if="isSelected(item.id)"></i>
              </div>
              <span class="paid-badge position-absolute" v-else>
                Pagada
              </span>
            </div>
            <div class="concept-price fw-bold mt-2">
              <template v-if="item.paidStatus === 'partial'">
                <span class="text-decoration-line-through text-muted fs-6 me-2">${{ parseFloat(item.amount || 0).toFixed(2) }}</span>
                ${{ parseFloat(item.remainingAmount || 0).toFixed(2) }}
                <small class="text-muted d-block fs-7 mt-1 fw-normal">Faltan por pagar: ${{ parseFloat(item.remainingAmount || 0).toFixed(2) }}</small>
              </template>
              <template v-else-if="item.paidStatus === 'paid'">
                <span class="text-decoration-line-through text-muted fs-6">${{ parseFloat(item.amount || 0).toFixed(2) }}</span>
              </template>
              <template v-else>
                ${{ parseFloat(item.amount || 0).toFixed(2) }}
              </template>
            </div>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<script setup>
import moment from 'moment';

const props = defineProps({
  concepts: {
    type: Array,
    default: () => []
  },
  modelValue: {
    type: Array,
    default: () => []
  },
  loading: {
    type: Boolean,
    default: false
  },
  totalPaid: {
    type: [Number, String],
    default: 0
  }
});

const emit = defineEmits(['update:modelValue']);

const isSelected = (id) => {
  return props.modelValue.includes(id);
};

const isItemAvailable = (item, index) => {
  // El primer item siempre está disponible
  if (index === 0) return true;
  
  // Un item está disponible si todos los items anteriores están seleccionados o ya están pagados
  for (let i = 0; i < index; i++) {
    const prevItem = props.concepts[i];
    if (prevItem.paidStatus !== 'paid' && !isSelected(prevItem.id)) {
      return false;
    }
  }
  return true;
};

const formatDueDate = (dateStr) => {
  if (!dateStr) return '';
  return moment(dateStr).format('DD/MM/YYYY');
};

const toggleSelection = (item) => {
  const itemIndex = props.concepts.findIndex(c => c.id === item.id);
  
  // No permitir seleccionar items no disponibles
  if (!isItemAvailable(item, itemIndex)) {
    return;
  }
  
  let updatedSelection = [...props.modelValue];
  const index = updatedSelection.indexOf(item.id);
  
  if (index > -1) {
    // Si se deselecciona el primer item, reiniciar toda la selección
    if (itemIndex === 0) {
      updatedSelection = [];
    } else {
      // Si se deselecciona un item intermedio, eliminar ese y todos los posteriores
      updatedSelection = updatedSelection.filter(id => {
        const conceptIndex = props.concepts.findIndex(c => c.id === id);
        return conceptIndex < itemIndex;
      });
    }
  } else {
    updatedSelection.push(item.id);
  }
  
  emit('update:modelValue', updatedSelection);
};
</script>

<style scoped>
.section-title {
  color: #5a122d;
  font-size: 1.1rem;
}

.section-icon {
  color: #5a122d;
  font-size: 1.2rem;
}

.text-muted-mini {
  font-size: 0.78rem;
  color: #828282;
}

.concept-card {
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  background-color: #fcfcfe;
  cursor: pointer;
  transition: all 0.2s ease;
  min-height: 100px;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.01);
}

.concept-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 10px rgba(90, 18, 45, 0.05);
  border-color: #5a122d;
}

.concept-card.selected {
  background-color: #fdf2f4;
  border-color: #5a122d;
  box-shadow: 0 4px 12px rgba(90, 18, 45, 0.08);
}

.concept-name {
  font-size: 0.95rem;
  color: #1a202c;
}

.concept-card.selected .concept-name {
  color: #5a122d;
}

.concept-due-date {
  font-size: 0.75rem;
}

.concept-price {
  font-size: 1.15rem;
  color: #4a5568;
}

.concept-card.selected .concept-price {
  color: #1a202c;
}

.check-indicator {
  width: 20px;
  height: 20px;
  border: 1px solid #cbd5e0;
  border-radius: 50%;
  transition: all 0.2s ease;
  background-color: white;
}

.concept-card.selected .check-indicator {
  border-color: #5a122d;
  background-color: #5a122d;
}

.check-icon {
  color: white;
  font-size: 0.65rem;
}

/* Disabled items styles */
.concept-card.disabled {
  opacity: 0.5;
  cursor: not-allowed;
  pointer-events: none;
}

.concept-card.disabled:hover {
  transform: none;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.01);
  border-color: #e2e8f0;
}

/* Paid card styles */
.concept-card.paid {
  background-color: #f0fdf4 !important;
  border-color: #22c55e !important;
  cursor: not-allowed;
  pointer-events: none;
}

.concept-card.paid .concept-name {
  color: #166534;
}

.paid-badge {
  top: 10px;
  right: 10px;
  background-color: #22c55e;
  color: white;
  font-size: 0.68rem;
  padding: 3px 8px;
  border-radius: 20px;
  font-weight: bold;
}

/* Skeleton Loading styles */
.skeleton-card {
  pointer-events: none;
  background-color: #f7fafc;
}

.skeleton-line {
  background-color: rgba(0, 0, 0, 0.05);
  border-radius: 4px;
  animation: pulse 1.5s ease-in-out infinite;
}

@keyframes pulse {
  0%, 100% {
    opacity: 0.6;
  }
  50% {
    opacity: 1;
  }
}
</style>
